import { useState, useEffect, useCallback } from 'react';
import { Q } from '@nozbe/watermelondb';
import {
  getDemoDatabase,
  seedDemoProducts,
  DemoProduct,
  DEMO_PRODUCTS,
} from '../../models/demo';
import type { ProductVariant } from '../../redux/productSlice';

export interface DemoProductItem {
  _id: string;
  bp_owner_id: string;
  bp_product_location_id: string;
  bp_product_barcode: string;
  bp_product_name: string;
  bp_product_category: string;
  bp_product_sub_category: string;
  bp_product_price_type: 'item' | 'unit';
  bp_product_unit_of_mass: string;
  bp_product_mass: string;
  bp_product_buying_price: number | null;
  bp_product_selling_price: number | null;
  bp_product_quantity: number | null;
  bp_product_size: string;
  bp_minimum_stock_level: number;
  bp_product_model: string;
  bp_product_image: string;
  bp_product_deleted: boolean;
  bp_product_is_favourite: boolean;
  bp_product_created_at: number;
  bp_product_updated_at: number;
  bp_product_has_variants: boolean;
  bp_product_variant_attributes: string[];
  bp_product_variants: ProductVariant[];
}

interface UseDemoProductsResult {
  products: DemoProductItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  searchProducts: (query: string) => Promise<void>;
  filterByCategory: (category: string | null) => Promise<void>;
  getCategories: () => string[];
}

export function useDemoProducts(): UseDemoProductsResult {
  const [products, setProducts] = useState<DemoProductItem[]>([]);
  const [allProducts, setAllProducts] = useState<DemoProductItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const normalizePriceType = (value: string): 'item' | 'unit' => {
    if (value === 'unit' || value === 'per_mass') {
      return 'unit';
    }
    return 'item';
  };

  const convertToProductItem = (model: DemoProduct): DemoProductItem => {
    const hasVariants = Boolean(model.bpProductHasVariants);
    return {
      _id: model.id,
      bp_owner_id: model.bpOwnerId,
      bp_product_location_id: model.bpProductLocationId,
      bp_product_barcode: model.bpProductBarcode,
      bp_product_name: model.bpProductName,
      bp_product_category: model.bpProductCategory,
      bp_product_sub_category: model.bpProductSubCategory,
      bp_product_price_type: normalizePriceType(model.bpProductPriceType),
      bp_product_unit_of_mass: model.bpProductUnitOfMass,
      bp_product_mass: model.bpProductMass,
      // Match real ProductObject: variant parents expose null at root price/qty
      bp_product_buying_price: hasVariants ? null : model.bpProductBuyingPrice,
      bp_product_selling_price: hasVariants
        ? null
        : model.bpProductSellingPrice,
      bp_product_quantity: hasVariants ? null : model.bpProductQuantity,
      bp_product_size: model.bpProductSize,
      bp_minimum_stock_level: model.bpMinimumStockLevel,
      bp_product_model: model.bpProductModel,
      bp_product_image: model.bpProductImage,
      bp_product_deleted: model.bpProductDeleted,
      bp_product_is_favourite: model.bpProductIsFavourite,
      bp_product_created_at: model.bpProductCreatedAt,
      bp_product_updated_at: model.bpProductUpdatedAt,
      bp_product_has_variants: hasVariants,
      bp_product_variant_attributes: model.getVariantAttributes(),
      bp_product_variants: model.getVariants(),
    };
  };

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Ensure demo products are seeded first
      await seedDemoProducts();

      const db = getDemoDatabase();
      const productsCollection = db.get<DemoProduct>('demo_products');
      const fetchedProducts = await productsCollection
        .query(Q.where('bp_product_deleted', false))
        .fetch();

      const catalogOrder = new Map(
        DEMO_PRODUCTS.map((p, index) => [p.bp_product_name, index]),
      );
      const productItems = fetchedProducts
        .map(convertToProductItem)
        .sort((a, b) => {
          const ai = catalogOrder.get(a.bp_product_name) ?? Number.MAX_SAFE_INTEGER;
          const bi = catalogOrder.get(b.bp_product_name) ?? Number.MAX_SAFE_INTEGER;
          return ai - bi;
        });
      setAllProducts(productItems);
      setProducts(productItems);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load demo products';
      setError(errorMessage);
      console.error('[useDemoProducts] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const searchProducts = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setProducts(allProducts);
        return;
      }

      const lowerQuery = query.toLowerCase();
      const filtered = allProducts.filter(
        product =>
          product.bp_product_name.toLowerCase().includes(lowerQuery) ||
          product.bp_product_barcode.includes(query) ||
          product.bp_product_category.toLowerCase().includes(lowerQuery),
      );
      setProducts(filtered);
    },
    [allProducts],
  );

  const filterByCategory = useCallback(
    async (category: string | null) => {
      if (!category) {
        setProducts(allProducts);
        return;
      }

      const filtered = allProducts.filter(
        product => product.bp_product_category === category,
      );
      setProducts(filtered);
    },
    [allProducts],
  );

  const getCategories = useCallback((): string[] => {
    const categorySet = new Set(allProducts.map(p => p.bp_product_category));
    return Array.from(categorySet);
  }, [allProducts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return {
    products,
    isLoading,
    error,
    refetch: fetchProducts,
    searchProducts,
    filterByCategory,
    getCategories,
  };
}

// Get a single product by ID
export async function getDemoProductById(
  productId: string,
): Promise<DemoProductItem | null> {
  try {
    const normalizePriceType = (value: string): 'item' | 'unit' => {
      if (value === 'unit' || value === 'per_mass') {
        return 'unit';
      }
      return 'item';
    };
    const db = getDemoDatabase();
    const productsCollection = db.get<DemoProduct>('demo_products');
    const product = await productsCollection.find(productId);
    const hasVariants = Boolean(product.bpProductHasVariants);
    return {
      _id: product.id,
      bp_owner_id: product.bpOwnerId,
      bp_product_location_id: product.bpProductLocationId,
      bp_product_barcode: product.bpProductBarcode,
      bp_product_name: product.bpProductName,
      bp_product_category: product.bpProductCategory,
      bp_product_sub_category: product.bpProductSubCategory,
      bp_product_price_type: normalizePriceType(product.bpProductPriceType),
      bp_product_unit_of_mass: product.bpProductUnitOfMass,
      bp_product_mass: product.bpProductMass,
      bp_product_buying_price: hasVariants ? null : product.bpProductBuyingPrice,
      bp_product_selling_price: hasVariants
        ? null
        : product.bpProductSellingPrice,
      bp_product_quantity: hasVariants ? null : product.bpProductQuantity,
      bp_product_size: product.bpProductSize,
      bp_minimum_stock_level: product.bpMinimumStockLevel,
      bp_product_model: product.bpProductModel,
      bp_product_image: product.bpProductImage,
      bp_product_deleted: product.bpProductDeleted,
      bp_product_is_favourite: product.bpProductIsFavourite,
      bp_product_created_at: product.bpProductCreatedAt,
      bp_product_updated_at: product.bpProductUpdatedAt,
      bp_product_has_variants: hasVariants,
      bp_product_variant_attributes: product.getVariantAttributes(),
      bp_product_variants: product.getVariants(),
    };
  } catch (err) {
    console.error('[getDemoProductById] Error:', err);
    return null;
  }
}

// Update product stock (for demo cart operations)
export async function updateDemoProductStock(
  productId: string,
  quantityChange: number,
): Promise<boolean> {
  try {
    const db = getDemoDatabase();
    await db.write(async () => {
      const product = await db
        .get<DemoProduct>('demo_products')
        .find(productId);
      await product.update((p: DemoProduct) => {
        const currentQty = Number(p.bpProductQuantity ?? 0);
        p.bpProductQuantity = Math.max(0, currentQty - quantityChange);
        p.bpProductUpdatedAt = Date.now();
      });
    });
    return true;
  } catch (err) {
    console.error('[updateDemoProductStock] Error:', err);
    return false;
  }
}
